import React, { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import { useUsers } from '../../hooks/useUsers';
import { GET_MY_PROFILE } from '../../graphql/operations';
import DMItem from './DMItem';
import NewDMModal from './NewDMModal';
import './DMSection.css';

function DMSection({ selectedId, onSelect }) {
  const {
    dms,
    unreadCounts,
    archiveConversation,
    hideDirectMessage,
    toggleFavorite,
    toggleAI,
    aiToggleEnabled
  } = useContext(WorkspaceContext);

  const [showModal, setShowModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { userMap } = useUsers();

  // Resolve DM display name: use title if it maps to a known user,
  // otherwise try the other member's ID (creatorId or membership userId)
  const { data: profileData } = useQuery(GET_MY_PROFILE);
  const currentUserId = profileData?.getMyProfile?.username || '';

  const isGroupDm = (conversation) => Number(conversation?.memberCount || 0) > 2;

  const resolveTitle = (conv) => {
    // Per-viewer title computed server-side (resolves the OTHER participant's
    // displayName for the caller, regardless of who created the DM). Falls
    // back to legacy heuristics only when the backend hasn't populated it
    // yet (mixed deploy windows or older cached data).
    const viewerTitle = (conv.viewerTitle || '').trim();
    if (viewerTitle) return viewerTitle;

    const title = conv.title || '';
    if (isGroupDm(conv)) {
      if (title) return title;
      const count = Number(conv?.memberCount || 0);
      return count > 0 ? `Group DM (${count})` : 'Group Direct Message';
    }
    // Legacy fallback (pre-viewerTitle). Kept narrow so we don't reintroduce
    // the self-name bug — only used when viewerTitle is empty.
    if (title) {
      const user = userMap[title];
      if (user) return user.displayName || title;
      const matchByDisplay = Object.values(userMap).find(
        (u) => u.displayName === title || u.username === title
      );
      if (matchByDisplay) return matchByDisplay.displayName || title;
      return title;
    }
    const otherId = conv.creatorId !== currentUserId ? conv.creatorId : null;
    if (otherId && userMap[otherId]) {
      return userMap[otherId].displayName || otherId;
    }
    if (otherId) return otherId;
    return 'Direct Message';
  };

  const getUserPresence = (conv) => {
    if (isGroupDm(conv)) return null;
    // Prefer the server-resolved counterpart id (clean lookup; immune to
    // title/displayName ambiguity).
    const counterpartId = (conv.viewerCounterpartId || '').trim();
    if (counterpartId && userMap[counterpartId]) return userMap[counterpartId];

    const title = conv.title || '';
    if (title && userMap[title]) return userMap[title];
    const matchByDisplay = Object.values(userMap).find(
      (u) => u.displayName === title || u.username === title
    );
    if (matchByDisplay) return matchByDisplay;
    const otherId = conv.creatorId !== currentUserId ? conv.creatorId : null;
    if (otherId && userMap[otherId]) return userMap[otherId];
    return null;
  };

  const handleToggleAI = (conversationId) => {
    const conv = dms.find((dm) => dm.id === conversationId);
    if (conv?.aiEnabled) {
      if (!window.confirm('Disable AI assistance for this conversation?')) {
        return;
      }
    }
    toggleAI(conversationId);
  };

  return (
    <div className="dm-section">
      <div className="section-header">
        <button
          type="button"
          className="section-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          <svg
            className={`section-chevron ${collapsed ? 'collapsed' : ''}`}
            viewBox="0 0 16 16"
            width="10"
            height="10"
            fill="currentColor"
          >
            <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.708z" />
          </svg>
          <span className="section-label">Direct Messages</span>
        </button>
        <button
          className="section-add-btn"
          onClick={() => setShowModal(true)}
          aria-label="New direct message"
          title="New DM"
        >
          +
        </button>
      </div>
      {!collapsed && (
        <div className="section-list">
          {dms.map((conv) => (
            <DMItem
              key={conv.id}
              conversation={conv}
              displayName={resolveTitle(conv)}
              isGroupDm={isGroupDm(conv)}
              groupMemberCount={Number(conv?.memberCount || 0)}
              isSelected={selectedId === conv.id}
              onSelect={onSelect}
              unreadCount={unreadCounts[conv.id] || 0}
              onToggleFavorite={toggleFavorite}
              onToggleAI={aiToggleEnabled ? handleToggleAI : undefined}
              onArchive={archiveConversation}
              onHide={hideDirectMessage}
              isFavorited={false}
              userPresence={getUserPresence(conv)}
            />
          ))}
          {dms.length === 0 && (
            <span className="section-empty">No conversations yet</span>
          )}
        </div>
      )}
      {showModal && <NewDMModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

export default DMSection;
